import React, { createContext, useCallback, useContext, useRef } from 'react'

type Listener = ((result: unknown) => void) | undefined

/**
 * This context is used to pass a function to a modal that can be called when the modal is closed.
 * Its invoked by the GlobalModalHost when close is called.
 * A separate context/provider is used to ensure that the state is cleanly passed around react and available freely in
 * the app.
 *
 * Note (as also noted in the GlobalModalHost): Only one modal is allowed to be open at a time.
 */
const ModalListenerContext = createContext<{
  setListener: (fn: Listener) => void
  callListener: (result: unknown) => void
  clearListener: () => void
}>({
  setListener: () => {},
  callListener: () => {},
  clearListener: () => {}
})

export const ModalListenerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const listenerStackRef = useRef<Listener[]>([])

  const setListener = useCallback((fn: Listener) => {
    listenerStackRef.current.push(fn ?? (() => {}))
  }, [])

  const callListener = useCallback((result: unknown) => {
    const fn = listenerStackRef.current.pop()
    fn?.(result)
  }, [])

  const clearListener = useCallback(() => {
    listenerStackRef.current.pop()
  }, [])

  return <ModalListenerContext.Provider value={{ setListener, callListener, clearListener }}>{children}</ModalListenerContext.Provider>
}

export const useModalListener = () => useContext(ModalListenerContext)
